import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import { Input } from './ui/input.jsx';
import { Label } from './ui/label.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select.jsx';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Search, Shield } from 'lucide-react';

// ─── constants ───────────────────────────────────────────────────────────────

const PERCENTILES = ['50', '90', '95', '99'];
// Memory risk slider (policy.mem_peak_floor) — how much of a workload's burst
// is RESERVED in the request vs left to the limit. Scoped policies need their
// own tier: a payments namespace and a batch namespace should not share one.
const MEM_PEAK_FLOORS = [
  { value: 'p99_peak', label: 'Peak p99 — safest (default)',
    hint: 'Reserves nearly every observed burst.' },
  { value: 'p95_peak', label: 'Peak p95 — balanced',
    hint: 'Reserves 95% of observed bursts; the rest uses limit headroom.' },
  { value: 'usage',    label: 'Working set — densest',
    hint: 'Reserves typical usage only; bursts run into the limit. Base percentile decides the number.' },
];
const STRATEGIES = ['patch', 'ssa', 'webhook'];
// W1.4 — operational mode (lifted from OptiPod's PolicyMode):
//   Auto      — recommendations are auto-applied (legacy default)
//   Recommend — recommendations surface only; never auto-apply
//   Disabled  — policy matches but no recommendations are emitted
const MODES = ['Auto', 'Recommend', 'Disabled'];
const ROLLOUT_STRATEGIES = ['immediate', 'onNextRestart'];

const DEFAULT_FORM = {
  name: '',
  namespace_selector: '',
  label_selector: '{}',
  min_cpu_cores: '0.05',
  max_cpu_cores: '32',
  min_memory_bytes: '268435456',
  max_memory_bytes: '68719476736',
  base_percentile: '90',
  mem_peak_floor: 'p99_peak',
  gradual_max_decrease_per_cycle_pct: '0.25',
  gradual_min_decrease_threshold_pct: '0.10',
  gradual_max_total_decrease_from_original: '0.50',
  apply_strategy: 'patch',
  training_window_days: '7',
  rightsize_stateful: false,
  min_replicas: '1',
  priority: '0',
  // W1.4 — default to Auto (legacy auto-apply) so existing flows remain
  // bit-identical. The dashboard's "create new policy" CTA may default
  // to Recommend later when OPTOPS_POLICY_V2_ENABLED ships.
  mode: 'Auto',
  weight: '100',
  rollout_strategy: 'onNextRestart',
};

const MIN_TRAINING_WINDOW_DAYS = 1;
const MAX_TRAINING_WINDOW_DAYS = 30;

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '—';
  const n = Number(bytes);
  if (n >= 1073741824) return `${(n / 1073741824).toFixed(1)} GiB`;
  if (n >= 1048576) return `${(n / 1048576).toFixed(0)} MiB`;
  return `${n} B`;
}

function strategyColor(s) {
  return s === 'ssa'
    ? 'bg-purple-100 text-purple-800'
    : s === 'webhook'
    ? 'bg-blue-100 text-blue-800'
    : 'bg-gray-100 text-gray-800';
}

// ─── validation ──────────────────────────────────────────────────────────────

function validateForm(f) {
  const errors = {};
  if (!f.name.trim()) errors.name = 'Name is required';
  if (!f.namespace_selector.trim() && f.label_selector.trim() === '{}') {
    errors.namespace_selector = 'At least one of namespace_selector or label_selector must be set';
  }
  try { JSON.parse(f.label_selector); } catch {
    errors.label_selector = 'Must be valid JSON object';
  }
  const minCpu = parseFloat(f.min_cpu_cores);
  const maxCpu = parseFloat(f.max_cpu_cores);
  if (isNaN(minCpu) || minCpu <= 0) errors.min_cpu_cores = 'Must be > 0';
  if (isNaN(maxCpu) || maxCpu <= 0) errors.max_cpu_cores = 'Must be > 0';
  if (!isNaN(minCpu) && !isNaN(maxCpu) && minCpu >= maxCpu)
    errors.max_cpu_cores = 'max_cpu must be > min_cpu';
  const minMem = parseInt(f.min_memory_bytes, 10);
  const maxMem = parseInt(f.max_memory_bytes, 10);
  if (isNaN(minMem) || minMem <= 0) errors.min_memory_bytes = 'Must be > 0';
  if (isNaN(maxMem) || maxMem <= 0) errors.max_memory_bytes = 'Must be > 0';
  if (!isNaN(minMem) && !isNaN(maxMem) && minMem >= maxMem)
    errors.max_memory_bytes = 'max_memory must be > min_memory';
  if (!PERCENTILES.includes(String(f.base_percentile)))
    errors.base_percentile = 'Must be 50, 90, or 99';
  const maxDec = parseFloat(f.gradual_max_decrease_per_cycle_pct);
  if (isNaN(maxDec) || maxDec <= 0 || maxDec > 1)
    errors.gradual_max_decrease_per_cycle_pct = 'Must be in (0, 1]';
  const minDec = parseFloat(f.gradual_min_decrease_threshold_pct);
  if (isNaN(minDec) || minDec <= 0 || minDec > 1)
    errors.gradual_min_decrease_threshold_pct = 'Must be in (0, 1]';
  const totalDec = parseFloat(f.gradual_max_total_decrease_from_original);
  if (isNaN(totalDec) || totalDec <= 0 || totalDec > 1)
    errors.gradual_max_total_decrease_from_original = 'Must be in (0, 1]';
  if (!STRATEGIES.includes(f.apply_strategy))
    errors.apply_strategy = 'Must be patch, ssa, or webhook';
  const trainingWindow = parseInt(f.training_window_days, 10);
  if (
    isNaN(trainingWindow) ||
    trainingWindow < MIN_TRAINING_WINDOW_DAYS ||
    trainingWindow > MAX_TRAINING_WINDOW_DAYS
  ) {
    errors.training_window_days = `Must be an integer in [${MIN_TRAINING_WINDOW_DAYS}, ${MAX_TRAINING_WINDOW_DAYS}]`;
  }
  const minReplicas = parseInt(f.min_replicas, 10);
  if (isNaN(minReplicas) || minReplicas < 1 || minReplicas > 32) {
    errors.min_replicas = 'Must be an integer in [1, 32]';
  }
  if (isNaN(parseInt(f.priority, 10))) errors.priority = 'Must be an integer';
  // W1.4 — mode / weight / rollout_strategy validation (mirrors backend
  // policy_service._validate_policy_fields).
  if (!MODES.includes(f.mode)) {
    errors.mode = `Must be one of ${MODES.join(', ')}`;
  }
  const weight = parseInt(f.weight, 10);
  if (isNaN(weight) || weight < 1 || weight > 1000) {
    errors.weight = 'Must be an integer in [1, 1000]';
  }
  if (!ROLLOUT_STRATEGIES.includes(f.rollout_strategy)) {
    errors.rollout_strategy = `Must be one of ${ROLLOUT_STRATEGIES.join(', ')}`;
  }
  return errors;
}

function formToPayload(f, clusterId) {
  let labelSelector = null;
  try {
    const parsed = JSON.parse(f.label_selector);
    if (Object.keys(parsed).length > 0) labelSelector = parsed;
  } catch { /* ignore */ }
  return {
    cluster_id: clusterId,
    name: f.name.trim(),
    namespace_selector: f.namespace_selector.trim() || null,
    label_selector: labelSelector,
    min_cpu_cores: parseFloat(f.min_cpu_cores),
    max_cpu_cores: parseFloat(f.max_cpu_cores),
    min_memory_bytes: parseInt(f.min_memory_bytes, 10),
    max_memory_bytes: parseInt(f.max_memory_bytes, 10),
    base_percentile: parseInt(f.base_percentile, 10),
    mem_peak_floor: f.mem_peak_floor,
    gradual_decrease: {
      max_decrease_per_cycle_pct: parseFloat(f.gradual_max_decrease_per_cycle_pct),
      min_decrease_threshold_pct: parseFloat(f.gradual_min_decrease_threshold_pct),
      max_total_decrease_from_original: parseFloat(f.gradual_max_total_decrease_from_original),
    },
    apply_strategy: f.apply_strategy,
    training_window_days: parseInt(f.training_window_days, 10),
    rightsize_stateful: !!f.rightsize_stateful,
    min_replicas: parseInt(f.min_replicas, 10),
    priority: parseInt(f.priority, 10),
    // W1.4
    mode: f.mode,
    weight: parseInt(f.weight, 10),
    rollout_strategy: f.rollout_strategy,
  };
}

// Template payloads use the same shape as a stored policy, so we can reuse
// policyToForm. Wrapper here exists so the call site reads as intent.
function templateToForm(template, overrideNamespace) {
  const p = (template && template.policy) || {};
  const form = policyToForm(p);
  if (overrideNamespace && !form.namespace_selector) {
    form.namespace_selector = overrideNamespace;
  }
  return form;
}


function policyToForm(p) {
  const gd = p.gradual_decrease || {};
  return {
    name: p.name || '',
    namespace_selector: p.namespace_selector || '',
    label_selector: JSON.stringify(p.label_selector || {}),
    min_cpu_cores: String(p.min_cpu_cores ?? '0.05'),
    max_cpu_cores: String(p.max_cpu_cores ?? '32'),
    min_memory_bytes: String(p.min_memory_bytes ?? '268435456'),
    max_memory_bytes: String(p.max_memory_bytes ?? '68719476736'),
    base_percentile: String(p.base_percentile ?? '90'),
    mem_peak_floor: p.mem_peak_floor || 'p99_peak',
    gradual_max_decrease_per_cycle_pct: String(gd.max_decrease_per_cycle_pct ?? '0.25'),
    gradual_min_decrease_threshold_pct: String(gd.min_decrease_threshold_pct ?? '0.10'),
    gradual_max_total_decrease_from_original: String(gd.max_total_decrease_from_original ?? '0.50'),
    apply_strategy: p.apply_strategy || 'patch',
    training_window_days: String(p.training_window_days ?? '7'),
    rightsize_stateful: !!p.rightsize_stateful,
    min_replicas: String(p.min_replicas ?? '1'),
    priority: String(p.priority ?? '0'),
    // W1.4 — mode/weight/rollout_strategy. Legacy policies (pre-W1.1)
    // load with safe defaults so the form renders without errors.
    mode: p.mode || 'Auto',
    weight: String(p.weight ?? '100'),
    rollout_strategy: p.rollout_strategy || 'onNextRestart',
  };
}

// ─── PolicyForm modal (tasks 13.1 + 13.2) ────────────────────────────────────

function PolicyForm({ clusterId, editingPolicy, onClose, onSaved, initialForm }) {
  const [form, setForm] = useState(
    initialForm || (editingPolicy ? policyToForm(editingPolicy) : DEFAULT_FORM)
  );
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateForm(form);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setSaving(true);
    try {
      const payload = formToPayload(form, clusterId);
      if (editingPolicy) {
        await axios.put(`${API}/rightsizing/policies/${editingPolicy.policy_id || editingPolicy._id}`, payload);
        toast.success('Policy updated');
      } else {
        await axios.post(`${API}/rightsizing/policies`, payload);
        toast.success('Policy created');
      }
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 422 && detail) {
        if (Array.isArray(detail)) {
          const fieldErrors = {};
          detail.forEach(d => {
            const field = d.loc?.[d.loc.length - 1];
            if (field) fieldErrors[field] = d.msg;
          });
          setErrors(fieldErrors);
        } else {
          toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
        }
      } else {
        toast.error('Failed to save policy');
      }
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, type = 'text', hint = null) => (
    <div>
      <Label htmlFor={key}>{label}</Label>
      <Input
        id={key}
        type={type}
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        className={`mt-1 ${errors[key] ? 'border-red-500' : ''}`}
        step={type === 'number' ? 'any' : undefined}
      />
      {errors[key] && <p className="text-xs text-red-500 mt-1">{errors[key]}</p>}
      {hint && !errors[key] && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-surface-dark rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-surface-border">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            {editingPolicy ? 'Edit Policy' : 'Create Policy'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            {field('name', 'Policy Name')}
            {field('priority', 'Priority', 'number', 'Higher value wins on overlap')}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {field('namespace_selector', 'Namespace Selector', 'text', 'Glob pattern, e.g. production-*')}
            <div>
              <Label htmlFor="label_selector">Label Selector (JSON)</Label>
              <Input
                id="label_selector"
                value={form.label_selector}
                onChange={e => set('label_selector', e.target.value)}
                className={`mt-1 font-mono text-sm ${errors.label_selector ? 'border-red-500' : ''}`}
                placeholder='{"app": "frontend"}'
              />
              {errors.label_selector && <p className="text-xs text-red-500 mt-1">{errors.label_selector}</p>}
              {errors.namespace_selector && !errors.label_selector && (
                <p className="text-xs text-red-500 mt-1">{errors.namespace_selector}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {field('min_cpu_cores', 'Min CPU (cores)', 'number', 'e.g. 0.05')}
            {field('max_cpu_cores', 'Max CPU (cores)', 'number', 'e.g. 32')}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {field('min_memory_bytes', 'Min Memory (bytes)', 'number', '268435456 = 256 MiB')}
            {field('max_memory_bytes', 'Max Memory (bytes)', 'number', '68719476736 = 64 GiB')}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Base Percentile</Label>
              <Select value={form.base_percentile} onValueChange={v => set('base_percentile', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERCENTILES.map(p => <SelectItem key={p} value={p}>P{p}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.base_percentile && <p className="text-xs text-red-500 mt-1">{errors.base_percentile}</p>}
            </div>
            <div>
              <Label>Memory Floor</Label>
              <Select value={form.mem_peak_floor} onValueChange={v => set('mem_peak_floor', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MEM_PEAK_FLOORS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                {(MEM_PEAK_FLOORS.find(o => o.value === form.mem_peak_floor) || {}).hint}
              </p>
            </div>
            <div>
              <Label>Apply Strategy</Label>
              <Select value={form.apply_strategy} onValueChange={v => set('apply_strategy', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STRATEGIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.apply_strategy && <p className="text-xs text-red-500 mt-1">{errors.apply_strategy}</p>}
            </div>
          </div>

          {/* W1.4 — Mode + Weight + Rollout strategy (lifted from OptiPod). */}
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Mode &amp; Priority</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Mode</Label>
                <Select value={form.mode} onValueChange={v => set('mode', v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Auto: auto-apply. Recommend: emit, never apply. Disabled: skip workloads.
                </p>
                {errors.mode && <p className="text-xs text-red-500 mt-1">{errors.mode}</p>}
              </div>
              <div>
                <Label>Weight</Label>
                <Input
                  type="number"
                  value={form.weight}
                  onChange={e => set('weight', e.target.value)}
                  min={1}
                  max={1000}
                  className={`mt-1 ${errors.weight ? 'border-red-500' : ''}`}
                />
                <p className="text-xs text-slate-500 mt-1">1–1000. Higher wins on equal priority.</p>
                {errors.weight && <p className="text-xs text-red-500 mt-1">{errors.weight}</p>}
              </div>
              <div>
                <Label>Rollout Strategy</Label>
                <Select
                  value={form.rollout_strategy}
                  onValueChange={v => set('rollout_strategy', v)}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLLOUT_STRATEGIES.map(rs => (
                      <SelectItem key={rs} value={rs}>{rs}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  onNextRestart: patch annotations only. immediate: trigger rolling restart.
                </p>
                {errors.rollout_strategy && (
                  <p className="text-xs text-red-500 mt-1">{errors.rollout_strategy}</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Gradual Decrease Config</p>
            <div className="grid grid-cols-3 gap-4">
              {field('gradual_max_decrease_per_cycle_pct', 'Max/Cycle %', 'number', '0.25 = 25%')}
              {field('gradual_min_decrease_threshold_pct', 'Min Threshold %', 'number', '0.10 = 10%')}
              {field('gradual_max_total_decrease_from_original', 'Max Total %', 'number', '0.50 = 50%')}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Profile Window</p>
            <div className="grid grid-cols-2 gap-4">
              {field(
                'training_window_days',
                'Training Window (days)',
                'number',
                `How much history the profile builder learns from (${MIN_TRAINING_WINDOW_DAYS}–${MAX_TRAINING_WINDOW_DAYS}). Independent of how often decisions run (every 30 min).`
              )}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">StatefulSet Rightsizing</p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.rightsize_stateful}
                onChange={(e) => set('rightsize_stateful', e.target.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <span className="text-sm">Allow rightsizing StatefulSets matched by this policy</span>
                <p className="text-xs text-slate-500 mt-1">
                  Default OFF — opt-in per policy. Database / queue teams stay safe by default.
                  Requires K8s 1.27+ for in-place resize; otherwise falls back to rolling restart.
                </p>
              </div>
            </label>
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">High-Availability Floor</p>
            <div className="grid grid-cols-2 gap-4">
              {field(
                'min_replicas',
                'Minimum Replicas',
                'number',
                'Set ≥2 for prod policies — singletons get auto-scaled to this floor when rightsizing applies. Skipped automatically when an HPA owns the workload.'
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : editingPolicy ? 'Update Policy' : 'Create Policy'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── EffectivePolicy resolver panel ──────────────────────────────────────────

function EffectivePolicyPanel({ clusterId }) {
  const [namespace, setNamespace] = useState('');
  const [labelsJson, setLabelsJson] = useState('{}');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resolve = async () => {
    setError('');
    setResult(null);
    if (!namespace.trim()) { setError('Namespace is required'); return; }
    let labels = {};
    try { labels = JSON.parse(labelsJson); } catch { setError('Labels must be valid JSON'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        cluster_id: clusterId,
        namespace: namespace.trim(),
        labels: JSON.stringify(labels),
      });
      const res = await axios.get(`${API}/rightsizing/policies/effective?${params}`);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resolve effective policy');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Search className="w-4 h-4" />
          Resolve Effective Policy
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="eff-ns">Namespace</Label>
            <Input id="eff-ns" value={namespace} onChange={e => setNamespace(e.target.value)}
              placeholder="e.g. production" className="mt-1" />
          </div>
          <div>
            <Label htmlFor="eff-labels">Labels (JSON)</Label>
            <Input id="eff-labels" value={labelsJson} onChange={e => setLabelsJson(e.target.value)}
              placeholder='{"app": "api"}' className="mt-1 font-mono text-sm" />
          </div>
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
        <Button onClick={resolve} disabled={loading} variant="outline" size="sm">
          {loading ? 'Resolving…' : 'Resolve'}
        </Button>
        {result && (
          <div className="mt-3 p-4 bg-slate-50 dark:bg-background-dark rounded-lg border border-slate-200 dark:border-surface-border text-sm space-y-1">
            <p><span className="font-medium">Policy:</span> {result.name || '(default)'}</p>
            <p><span className="font-medium">Percentile:</span> P{result.base_percentile}</p>
            <p><span className="font-medium">Strategy:</span> {result.apply_strategy}</p>
            <p><span className="font-medium">CPU:</span> {result.min_cpu_cores} – {result.max_cpu_cores} cores</p>
            <p><span className="font-medium">Memory:</span> {formatBytes(result.min_memory_bytes)} – {formatBytes(result.max_memory_bytes)}</p>
            <p><span className="font-medium">Priority:</span> {result.priority}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function RightsizingPolicies({ clusterId }) {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [deleting, setDeleting] = useState(null);
  // Pre-fill the create-form from a template when the user lands here via
  // the "Apply this template" hint on the Recommendations page
  // (URL: /rightsizing?tab=policies&template=<id>&namespace=<ns>&workload=<wl>).
  const [prefillForm, setPrefillForm] = useState(null);

  const fetchPolicies = useCallback(async () => {
    if (!clusterId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rightsizing/policies?cluster_id=${clusterId}`);
      setPolicies(res.data || []);
    } catch {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  // One-shot URL-param handler: load the suggested template, hand it to the
  // form pre-populated, then strip the params so a refresh doesn't reopen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const templateId = params.get('template');
    if (!templateId) return;
    const namespace = params.get('namespace') || '';
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/rightsizing/policy-templates`);
        const templates = res.data?.templates || [];
        const template = templates.find(t => t.id === templateId);
        if (template && !cancelled) {
          setPrefillForm(templateToForm(template, namespace));
          setShowForm(true);
          setEditingPolicy(null);
          // Clean URL so a refresh doesn't reopen.
          const url = new URL(window.location.href);
          ['template', 'namespace', 'workload'].forEach(k => url.searchParams.delete(k));
          window.history.replaceState({}, '', url.toString());
        }
      } catch {
        // Non-fatal — user can still pick the template manually.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleDelete = async (policy) => {
    const id = policy.policy_id || policy._id;
    if (!window.confirm(`Delete policy "${policy.name}"?`)) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/rightsizing/policies/${id}`);
      toast.success('Policy deleted');
      fetchPolicies();
    } catch {
      toast.error('Failed to delete policy');
    } finally {
      setDeleting(null);
    }
  };

  const openCreate = () => { setPrefillForm(null); setEditingPolicy(null); setShowForm(true); };
  const openEdit = (p) => { setPrefillForm(null); setEditingPolicy(p); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditingPolicy(null); setPrefillForm(null); };
  const onSaved = () => { closeForm(); fetchPolicies(); };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Rightsizing Policies
              <Badge variant="outline" className="ml-1">{policies.length}</Badge>
            </CardTitle>
            <Button onClick={openCreate} size="sm" className="flex items-center gap-1">
              <Plus className="w-4 h-4" />
              Create Policy
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <div key={i} className="skeleton h-12 rounded" />)}
            </div>
          ) : policies.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <Shield className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>No policies configured. Create one to override global defaults.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-surface-border text-left text-slate-500 dark:text-text-secondary">
                    <th className="pb-3 pr-4 font-medium">Name</th>
                    <th className="pb-3 pr-4 font-medium">Namespace Selector</th>
                    <th className="pb-3 pr-4 font-medium">Label Selector</th>
                    <th className="pb-3 pr-4 font-medium">Mode</th>
                    <th className="pb-3 pr-4 font-medium">Percentile</th>
                    <th className="pb-3 pr-4 font-medium">Strategy</th>
                    <th className="pb-3 pr-4 font-medium">Priority</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-surface-border">
                  {policies.map(p => {
                    const id = p.policy_id || p._id;
                    return (
                      <tr key={id} className="hover:bg-slate-50 dark:hover:bg-background-dark transition-colors">
                        <td className="py-3 pr-4 font-medium text-slate-900 dark:text-white">{p.name}</td>
                        <td className="py-3 pr-4 font-mono text-xs text-slate-600 dark:text-text-secondary">
                          {p.namespace_selector || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="py-3 pr-4 font-mono text-xs text-slate-600 dark:text-text-secondary">
                          {p.label_selector && Object.keys(p.label_selector).length > 0
                            ? JSON.stringify(p.label_selector)
                            : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="py-3 pr-4">
                          {/* W1.4 — mode badge. Auto = green (active), Recommend =
                              amber (read-only), Disabled = red (suppressed). */}
                          {(() => {
                            const mode = p.mode || 'Auto';
                            const cls = mode === 'Disabled'
                              ? 'bg-red-100 text-red-800'
                              : mode === 'Recommend'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-emerald-100 text-emerald-800';
                            return (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
                                {mode}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="py-3 pr-4">
                          <Badge variant="outline">P{p.base_percentile}</Badge>
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${strategyColor(p.apply_strategy)}`}>
                            {p.apply_strategy}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-slate-600 dark:text-text-secondary">{p.priority}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <button onClick={() => openEdit(p)}
                              className="text-slate-400 hover:text-blue-600 transition-colors" title="Edit">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(p)} disabled={deleting === id}
                              className="text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {clusterId && <EffectivePolicyPanel clusterId={clusterId} />}

      {showForm && (
        <PolicyForm
          clusterId={clusterId}
          editingPolicy={editingPolicy}
          onClose={closeForm}
          onSaved={onSaved}
          initialForm={prefillForm}
        />
      )}
    </div>
  );
}

export default RightsizingPolicies;
